/**
 * src/help/HelpEntry.js
 *
 * The two entry points into the single guided-help engine. Mounted once per
 * page (D&D Image and Professional). When help is inactive this renders ONLY
 * two small, restrained controls — the page is otherwise completely normal:
 *
 *   • a round START HERE button at the TOP-LEFT of the page content area
 *       → runs the full ordered tour from the beginning.
 *   • a HELP control opposite it at the TOP-RIGHT
 *       → opens a small topic chooser; picking a topic jumps the same engine
 *         straight to that area.
 *
 * There is NO first-visit prompt and NO bottom control: those corners remain
 * available to page content and the existing global Support button. Nothing about the tour
 * (dim, spotlight, yellow box, arrow, modal) appears until the user explicitly
 * clicks START HERE or HELP.
 */
import React, { useMemo, useState } from 'react';
import GuidedHelp from './GuidedHelp';
import { buildHelpManifest, buildHelpTopics } from './helpManifest';
import '../portalResponsive.css';

const YELLOW = '#FFDD00';
const YELLOW_DEEP = '#F5B800';
const INK = '#1A1500';

// Horizontal position that lines up with the left edge of the 1400px-max
// centred page content (container inner-left = 50vw - 700 + 40px padding),
// clamped so it never leaves the viewport on narrow desktops.
const CONTENT_LEFT = 'max(20px, calc(50vw - 660px))';
const CONTENT_RIGHT = 'max(20px, calc(50vw - 660px))';

export default function HelpEntry({ pageKey }) {
  const manifest = useMemo(() => buildHelpManifest(pageKey), [pageKey]);
  const topics = useMemo(() => buildHelpTopics(pageKey), [pageKey]);

  const [helpOpen, setHelpOpen] = useState(false);
  const [startId, setStartId] = useState(null);
  const [chooser, setChooser] = useState(false);

  function startTour() {
    setChooser(false);
    setStartId(null);
    setHelpOpen(true);
  }
  function startTopic(id) {
    setChooser(false);
    setStartId(id);
    setHelpOpen(true);
  }

  return (
    <>
      <GuidedHelp
        pageKey={pageKey}
        manifest={manifest}
        open={helpOpen}
        startId={startId}
        onClose={() => setHelpOpen(false)}
      />

      {/* Controls only exist while help is INACTIVE. */}
      {!helpOpen && (
        <>
          {/* Round START HERE — top-left of the page content area */}
          <button
            className="vi-help-start"
            data-help-ui="1"
            data-testid="start-here"
            onClick={startTour}
            aria-label="Start Here — guided tour of this page"
            title="Start Here — guided tour"
            style={{
              position: 'fixed',
              top: 200,
              left: CONTENT_LEFT,
              zIndex: 150,
              width: 88,
              height: 88,
              borderRadius: '50%',
              background: YELLOW,
              color: INK,
              border: `2px solid ${YELLOW_DEEP}`,
              cursor: 'pointer',
              boxShadow: '0 8px 22px rgba(255,221,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              lineHeight: 1,
            }}
          >
            <span className="vi-help-start-star" aria-hidden="true" style={{ fontSize: 23 }}>★</span>
            <span className="vi-help-start-label" style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.06em' }}>START HERE</span>
          </button>

          {/* HELP — opposite START HERE at the upper-right; opens inward/downward. */}
          <div className="vi-help-topics-wrap" data-help-ui="1" style={{ position: 'fixed', top: 222, right: CONTENT_RIGHT, zIndex: 150 }}>
            {chooser && (
              <div
                role="menu"
                aria-label="Help topics"
                style={{
                  position: 'absolute',
                  top: 52,
                  right: 0,
                  width: 240,
                  background: '#12131b',
                  border: `2px solid ${YELLOW_DEEP}`,
                  borderRadius: 14,
                  padding: 8,
                  boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: YELLOW, padding: '6px 10px 8px' }}>
                  What do you need help with?
                </div>
                {topics.map((t) => (
                  <button
                    key={t.key}
                    role="menuitem"
                    onClick={() => startTopic(t.startId)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
                      border: 'none', color: '#E7ECF3', fontSize: 14, fontWeight: 600,
                      padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,221,0,0.12)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            <button
              className="vi-help-topics-button"
              data-testid="help-topics"
              onClick={() => setChooser((c) => !c)}
              aria-label="Help topics"
              aria-expanded={chooser}
              style={{
                background: '#1b1c26',
                color: YELLOW,
                border: `1.5px solid ${YELLOW_DEEP}`,
                borderRadius: 999,
                padding: '11px 20px',
                fontSize: 14,
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 15 }}>?</span> Help
            </button>
          </div>
        </>
      )}
    </>
  );
}
